/**
 * Messages API client for messaging system.
 */

import apiClient from './client';
import type { 
  Message, 
  SendMessageRequest,
  MessagesPage,
  MessageThreadsPage,
  UnreadMessageCountResponse
} from '../types/message';

/**
 * Send a message to a recipient.
 * 
 * @param request - Send message request with content and recipient ID
 * @returns Promise with created message
 */
export const sendMessage = async (request: SendMessageRequest): Promise<Message> => {
  const response = await apiClient.post<Message>('/messages', request);
  return response.data;
};

/**
 * Get paginated message threads for the current user.
 * 
 * @param page - Page number (0-indexed)
 * @param size - Page size
 * @returns Promise with paginated message threads
 */
export const getThreads = async (
  page: number = 0, 
  size: number = 20
): Promise<MessageThreadsPage> => {
  const response = await apiClient.get<MessageThreadsPage>('/messages/threads', {
    params: { page, size },
  });
  return response.data;
};

/**
 * Get paginated messages in a thread.
 * 
 * @param threadId - Thread ID
 * @param page - Page number (0-indexed)
 * @param size - Page size
 * @returns Promise with paginated messages
 */
export const getMessages = async (
  threadId: string,
  page: number = 0, 
  size: number = 50
): Promise<MessagesPage> => {
  const response = await apiClient.get<MessagesPage>(`/messages/threads/${threadId}/messages`, {
    params: { page, size },
  });
  return response.data;
};

/**
 * Get unread message count for the current user.
 * 
 * @returns Promise with unread count
 */
export const getUnreadCount = async (): Promise<number> => {
  const response = await apiClient.get<UnreadMessageCountResponse>('/messages/unread/count');
  return response.data.unreadCount;
};

/**
 * Mark all messages in a thread as read.
 * 
 * @param threadId - Thread ID
 * @returns Promise that resolves when marked as read
 */
export const markThreadAsRead = async (threadId: string): Promise<void> => {
  await apiClient.put(`/messages/threads/${threadId}/read`);
};

/**
 * Delete a message thread and all its messages.
 *
 * @param threadId - Thread ID to delete
 * @returns Promise that resolves when deleted
 */
export const deleteThread = async (threadId: string): Promise<void> => {
  await apiClient.delete(`/messages/threads/${threadId}`);
};

/**
 * Record that the current user is typing in a thread.
 * Call this on every keypress (the hook debounces to ≤ once per 3 s).
 */
export const postTyping = async (threadId: string): Promise<void> => {
  await apiClient.post(`/messages/threads/${threadId}/typing`);
};

/**
 * Fetch the usernames of other participants currently typing in a thread.
 */
export const getTypingStatus = async (threadId: string): Promise<string[]> => {
  const response = await apiClient.get<{ typingUsers: string[] }>(
    `/messages/threads/${threadId}/typing`,
  );
  return response.data.typingUsers;
};
